import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { categoryService, CreateCategoryRequest } from '../../services/categoryService';
import { useToast } from '../UI/ToastProvider';
import { EmojiPicker } from './EmojiPicker';

interface CreateCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateCategoryModal: React.FC<CreateCategoryModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState<CreateCategoryRequest>({
    name: '',
    description: '',
    type: 'document',
    icon: '📄'
  });
  const [isLoading, setIsLoading] = useState(false);
  const { addToast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      addToast('Nome da categoria é obrigatório', 'error');
      return;
    }

    setIsLoading(true);
    try {
      await categoryService.createCategory(formData);
      addToast('Categoria criada com sucesso!', 'success');
      onSuccess();
      onClose();
      setFormData({ name: '', description: '', type: 'document', icon: '📄' });
    } catch (error) {
      addToast('Erro ao criar categoria. Tente novamente.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof CreateCategoryRequest, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Nova Categoria</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Nome *
            </label>
            <div className="relative">
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value.slice(0, 50))}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                  formData.name.length >= 50 ? 'border-red-300 focus:ring-red-500' : ''
                }`}
                placeholder="Digite o nome da categoria"
                required
                maxLength={50}
              />
              <div className="absolute -bottom-6 right-0 text-xs text-gray-500">
                {formData.name.length}/50
              </div>
            </div>
            {formData.name.length >= 50 && (
              <p className="text-xs text-red-500 mt-1">
                Limite máximo de 50 caracteres atingido
              </p>
            )}
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Descrição
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Digite uma descrição para a categoria"
            />
          </div>

          <div>
            <label htmlFor="icon" className="block text-sm font-medium text-gray-700 mb-1">
              Ícone
            </label>
            <EmojiPicker
              value={formData.icon}
              onChange={(emoji) => handleInputChange('icon', emoji)}
              placeholder="📄"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              disabled={isLoading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? 'Criando...' : 'Criar Categoria'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}; 